import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';

/**
 * Roles that exist ONLY in the design. The Technician was never given a screen
 * in the old shell — there, `/home` falls through to the doctor's home — so a
 * technician is on the design shell whatever this browser's switch says.
 */
/* ROLES WITH NOWHERE TO GO IN THE LEGACY SHELL. Neither holds a patient,
   appointment or billing permission, so the old sidebar offers them Home and
   Ask MAIE and nothing else — two doors and a dead end. Their whole job lives
   on the design shell, so they are always on it, flag or no flag. */
const DESIGN_ONLY_ROLES = new Set(['Technician', 'Stock Manager', 'Accountant']);
const designOnly = (role: string | null | undefined) => DESIGN_ONLY_ROLES.has(role ?? '');

/**
 * Whether this browser draws the app in the NEW design shell.
 *
 * On by default. `?maie=0` on any URL goes back to the old shell, `?maie=1`
 * returns. The answer sticks in localStorage, so it survives navigation and
 * reloads without keeping the query string around.
 *
 * WHY NOT A PLATFORM FLAG. A per-browser switch lets one person go back to
 * the old shell without moving the whole clinic. The clinic-level behaviour
 * changes are platform flags of their own (see the backend FLAG_REGISTRY).
 */

const KEY = 'maie-shell';

/**
 * Read `?maie=` once, at app start, BEFORE routing.
 *
 * The switch used to be read when the shell mounted, which is too late: opening
 * `/?maie=1` while signed out redirects to /login and drops the query string on
 * the way, so the parameter never reached the component that looks for it. Call
 * this from main.tsx before the router is created.
 */
export function initMaieShell(): void {
  try {
    const url = new URLSearchParams(window.location.search).get('maie');
    if (url === '1' || url === '0') localStorage.setItem(KEY, url);
  } catch {
    /* no localStorage — the design shell, the default, still draws */
  }
}

function read(): boolean {
  try {
    const url = new URLSearchParams(window.location.search).get('maie');
    if (url === '1' || url === '0') {
      localStorage.setItem(KEY, url);
      return url === '1';
    }
    return localStorage.getItem(KEY) !== '0';
  } catch {
    /* private mode, or no localStorage — the default, the design shell. */
    return true;
  }
}

export function useMaieShell(): boolean {
  const [on, setOn] = useState(read);
  const role = useAuthStore((s) => s.activeTenant?.role);

  useEffect(() => {
    // Another tab flipping it should not leave this one disagreeing.
    const sync = (e: StorageEvent) => {
      if (e.key === KEY) setOn(e.newValue !== '0');
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  return on || designOnly(role);
}

/** For code outside React that needs the same answer. */
export function isMaieShell(): boolean {
  return read() || designOnly(useAuthStore.getState().activeTenant?.role);
}
